import { ChartData, Chart as ChartJS, ChartTypeRegistry, LayoutPosition, Plugin, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import DoughnutLabel from "chartjs-plugin-doughnutlabel-v3";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { ReactNode } from "react";
import { Chart } from "react-chartjs-2";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { getCachedNumberFormat } from "../../../shared/utilty/number-format-cache";
import { CommonProps } from "../common-props";

declare let window: Window & { kuika: any };

/** Stroke style for a line-mode series; anything else renders as a solid line. */
export type ChartLineStyle = "solid" | "dashed" | "dotted";

/**
 * KOA-18057: one chart data series beyond the fixed first/second/third props.
 * The first three series keep their flat `*DataFieldKeyName` props (unchanged wire
 * format); series four and up arrive here so a chart can bind any number of series.
 * Field names mirror the flat props: `fieldKeyName` = value column,
 * `bgColorKeyName` = color column, `bgColor` = fixed literal color.
 */
export interface ChartSeriesInput {
  fieldKeyName?: string;
  bgColorKeyName?: string;
  bgColor?: string;
  legendLabel?: string;
  lineStyle?: ChartLineStyle | string;
}

export interface BaseChartProps {
  dataSource: any[];
  mode: keyof ChartTypeRegistry;
  leftAxis: boolean;
  rightAxis: boolean;
  beginAtZero: boolean;
  legend: LayoutPosition;
  legendAlign?: "start" | "center" | "end";
  legendFontSize?: number;
  dataLabelKeyName?: string;
  dataDisplayLabelKeyName?: string;
  dataTooltipFooterDataLabel?: string;
  firstDataFieldKeyName?: string;
  firstDataBgColorKeyName?: string;
  secondDataFieldKeyName?: string;
  secondDataBgColorKeyName?: string;
  thirdDataFieldKeyName?: string;
  thirdDataBgColorKeyName?: string;
  /** Fixed (literal) bg colors — used instead of the *BgColorKeyName column when the designer picks "Fixed". */
  firstDataBgColor?: string;
  secondDataBgColor?: string;
  thirdDataBgColor?: string;
  /** Fixed series (legend) labels — override the auto-derived data-field name when set. */
  firstDataLegendLabel?: string;
  secondDataLegendLabel?: string;
  thirdDataLegendLabel?: string;
  /** Per-series line style (line mode only) for the fixed first three series. */
  firstDataLineStyle?: ChartLineStyle | string;
  secondDataLineStyle?: ChartLineStyle | string;
  thirdDataLineStyle?: ChartLineStyle | string;
  /**
   * KOA-18057: chart data series beyond the fixed three. Each entry is rendered as
   * its own dataset after the first three, so a chart can show an unbounded number
   * of series (e.g. seven lines). Empty/absent → only the first three series render.
   */
  additionalSeries?: ChartSeriesInput[];
  fill?: boolean;
  style?: React.CSSProperties;
  maxYAxisValue?: number;
  /**
   * When set (and `maxYAxisValue` is not), the Y-axis maximum is auto-computed
   * as `max(data) * maxYAxisValueRatio` — e.g. 1.2 leaves 20% headroom above the
   * tallest value. An explicit `maxYAxisValue` always wins over the ratio.
   */
  maxYAxisValueRatio?: number;
  centerText?: string;
  children?: any;
  showDataLabels?: boolean;
  dataLabelFormatter?: string;
  dataLabelPosition?: "top" | "center" | "bottom";
  verticalDataLabels?: boolean;
  dataTargetLineKeyName?: string;
  multiTooltipContentField?: string;
  startAngle?: number;
  levelFrequency?: number;
  showLabelBoxes?: boolean;
  barThickness?: number;
  /** Bar mode only: stack the (up to 3) data series on top of each other instead of grouping them side by side. */
  stacked?: boolean;
  lineColor?: string;
  lineThickness?: number;
  lineDotWidth?: number;
  showLineLabel?: boolean;
  lineLabelColor?: string;
  lineLabelPosition?: "top" | "center" | "bottom";
  levelColor?: string;
  angleLinesColor?: string;
  pointLabelColor?: string;
  showLevelValueBoxes?: boolean;
  showLevelValues?: boolean;
  showPointLabels?: boolean;
  halfDonut?: boolean;
  /**
   * Design-time-only hint that controls the category set used by the
   * placeholder sample data when no `dataSource` is bound. Stripped from
   * generated apps (`isIgnoredOnGeneratedApp: true`).
   */
  categoryHint?: CategoryHint;
}

type TimeCategoryHint = "6-month" | "12-month" | "4-quarter" | "7-day" | "30-day" | "8-week" | "24-hour";
type BarsCategoryHint = `${3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}-bars`;
// Free-form comma-separated label list ("Türkiye,Almanya,Fransa") is also
// accepted at runtime for non-temporal placeholders with named categories.
// `(string & {})` keeps literal-union autocomplete while opening the type.
export type CategoryHint = "auto" | TimeCategoryHint | BarsCategoryHint | (string & {});

interface BaseChartState {}

type ChartDataType = ChartData<
  "bar" | "line" | "doughnut" | "radar" | "pie" | "bubble" | "polarArea" | "scatter"
>;

interface SampleSeries {
  labels: string[];
  values: number[];
}

interface ChartDatasetLike {
  data?: unknown[];
  type?: string;
  isTargetLine?: boolean;
}

interface NiceAxis {
  max: number;
  stepSize: number;
}

interface AxisTickFont {
  size: number;
  family: string;
}

interface AxisTicks {
  callback: (value: number | string) => string;
  stepSize?: number;
  color?: string;
  padding?: number;
  font?: AxisTickFont;
}

const SAMPLE_SERIES: Record<TimeCategoryHint, SampleSeries> = {
  "6-month": {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    values: [42, 58, 51, 73, 65, 84]
  },
  "12-month": {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    values: [42, 58, 51, 73, 65, 84, 92, 81, 76, 89, 95, 110]
  },
  "4-quarter": {
    labels: ["Q1", "Q2", "Q3", "Q4"],
    values: [180, 245, 290, 340]
  },
  "7-day": {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    values: [38, 52, 47, 61, 70, 55, 49]
  },
  "30-day": {
    labels: Array.from({ length: 30 }, (_v, i) => `${i + 1}`),
    values: [
      28, 34, 30, 41, 38, 45, 50, 47, 52, 60, 58, 64, 70, 68, 75, 80, 77, 82, 88, 85, 91, 96, 93, 99, 104, 101, 108,
      113, 110, 117
    ]
  },
  "8-week": {
    labels: ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8"],
    values: [120, 145, 132, 168, 175, 190, 182, 205]
  },
  "24-hour": {
    labels: ["00", "02", "04", "06", "08", "10", "12", "14", "16", "18", "20", "22"],
    values: [12, 8, 6, 14, 32, 58, 74, 82, 78, 65, 48, 28]
  }
};

const BARS_BASE_VALUES = [42, 58, 51, 73, 65, 84, 92, 81, 76, 89, 95, 110];

function resolveSampleSeries(hint: CategoryHint | undefined): SampleSeries {
  if (!hint || hint === "auto") return SAMPLE_SERIES["6-month"];
  if (hint.includes(",")) {
    const labels = hint
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (labels.length >= 2 && labels.length <= 12) {
      return {
        labels,
        values: BARS_BASE_VALUES.slice(0, labels.length)
      };
    }
  }
  const barsMatch = /^(\d+)-bars$/.exec(hint);
  if (barsMatch) {
    const n = Number(barsMatch[1]);
    if (n >= 3 && n <= 12) {
      return {
        labels: Array.from({ length: n }, (_v, i) => `Item ${i + 1}`),
        values: BARS_BASE_VALUES.slice(0, n)
      };
    }
  }
  return SAMPLE_SERIES[hint as TimeCategoryHint] ?? SAMPLE_SERIES["6-month"];
}

class BaseChart extends React.PureComponent<BaseChartProps & CommonProps, BaseChartState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kbasechart_${this.uniqueKey}`);

  private generateSecureId(): string {
    const guid = Guid.create();
    return `bar-chart-${guid.toString()}`;
  }

  private canvasId: string = this.generateSecureId();

  private colorMap: { [key: string]: string } = {};

  private colorPalette: string[] = ["#86D497", "#FFDF6E", "#689FF2", "#FF6A6A", "#FFA462", "#AB96FF", "#F596DA"];

  private currentColorIndex: number = 0;

  private customLabelBoxesPlugin: Plugin = {
    id: "customLabelBoxes",
    beforeDraw: (chart: any) => {
      if (this.props.mode !== "radar" || !this.props.showLabelBoxes) return;

      const ctx = chart.ctx;
      const { width, height } = chart.canvas;
      const chartArea = chart.chartArea;

      const centerX = chartArea.left + (chartArea.right - chartArea.left) / 2;
      const centerY = chartArea.top + (chartArea.bottom - chartArea.top) / 2;

      const scale = chart.scales.r;
      const labels = chart.data.labels;

      labels.forEach((label: string, i: number) => {
        const startAngle = -Math.PI / 2;
        const angle = startAngle + (i * 2 * Math.PI) / labels.length;

        const datasource = this.props.dataSource ? this.props.dataSource : [];

        const dataPoint = datasource[i];
        if (!dataPoint) return;

        const firstField = dataPoint[KMainFunctions.toCamelCase(this.props.firstDataFieldKeyName)];
        const secondField = dataPoint[KMainFunctions.toCamelCase(this.props.secondDataFieldKeyName)];

        if (firstField === undefined) return;

        const baseRadius = scale.drawingArea;
        const padding = 40;
        const radius = baseRadius + padding;

        let x = centerX + Math.cos(angle) * radius;
        let y = centerY + Math.sin(angle) * radius;

        const boxWidth = 96;
        const boxHeight = 40;

        let fill = "#FFEFC6";

        if (Math.cos(angle) > 0.5) {
          x += boxWidth / 1;
          y += boxHeight * 1.5;
          if (Math.sin(angle) < -0.3) {
            x -= boxWidth / 8;
            y += boxHeight / 1.5;
          }
          if (Math.sin(angle) < 0 && Math.cos(angle) > 0.9) {
            x += boxWidth / 16;
            y -= boxHeight / 1;
          }
          if (Math.sin(angle) > -0.3 && Math.sin(angle) < 0.4) {
            x += boxWidth / 16;
            y -= boxHeight / 1.5;
          }
          if (Math.sin(angle) < -0.4) {
            x += boxWidth / 2;
            y -= boxHeight / 8;
          }
        } else if (Math.cos(angle) < -0.5) {
          x -= boxWidth / 16;
          y += boxHeight * 1.5;

          if (Math.sin(angle) < -0.2) {
            x -= boxWidth / 4;
            y -= boxHeight / 2;
          }
          if (Math.sin(angle) < -0.4) {
            x -= boxWidth / 4;
            y += boxHeight / 1;
          }

          if (Math.sin(angle) > 0 && Math.cos(angle) < -0.9) {
            x += boxWidth / 16;
            y -= boxHeight / 1.5;
          }
        }

        if (Math.sin(angle) > 0.5) {
          y += boxHeight / 2;
          x -= boxWidth / 16;

          if (Math.sin(angle) > 0.2 && Math.cos(angle) > 0.5) {
            x += boxWidth / 8;
            y -= boxHeight / 0.75;
          } else if (Math.sin(angle) > 0.2 && Math.cos(angle) < -0.5) {
            x += boxWidth / 12;
            y -= boxHeight / 0.75;
          } else if (Math.sin(angle) > 0 && Math.cos(angle) < 1) {
            x += boxWidth / 2;
            y += boxHeight / 4;
          }
        } else if (Math.sin(angle) < -0.5) {
          y -= boxHeight / 1;
        }

        ctx.save();

        ctx.fillStyle = fill;
        ctx.strokeStyle = "#E6E6E6";

        ctx.beginPath();
        ctx.roundRect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight, 5);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#000";
        ctx.font = "10px Arial";
        ctx.textAlign = "center";
        ctx.fillText(`${this.props.firstDataFieldKeyName}: ${this.getFormattedValue(firstField)}`, x, y - 8);
        ctx.fillText(
          `${this.props.secondDataFieldKeyName}: ${secondField ? this.getFormattedValue(secondField) : "-"}`,
          x,
          y + 12
        );

        ctx.restore();
      });
    }
  };

  private halfDonutLabelPlugin: Plugin = {
    id: "halfDonutLabel",
    afterDraw: (chart: any) => {
      if (this.props.mode !== "doughnut" || !this.props.halfDonut) return;

      const labels = chart.options.plugins?.halfDonutLabel?.labels;
      if (!labels || labels.length === 0) return;

      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      const offsetY = (chartArea.bottom - chartArea.top) * 0.25;

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      let currentY = centerY + offsetY;
      const lineHeight = 1.2;

      labels.forEach((label: any) => {
        const fontSize = parseInt(label.font?.size) || 14;
        const fontWeight = label.font?.weight || "normal";
        const fontFamily = label.font?.family || "Arial";

        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = label.color || "black";
        ctx.fillText(label.text || "", centerX, currentY);

        currentY += fontSize * lineHeight;
      });

      ctx.restore();
    }
  };

  constructor(props: BaseChartProps) {
    super(props);
    this.state = {};
    ChartJS.register(...registerables);
    ChartJS.register(ChartDataLabels);
    ChartJS.register(DoughnutLabel);
  }

  componentDidMount(): void {
    this.setDynamicStyle();
    this.updatePluginRegistration();
  }

  componentDidUpdate(prevProps: BaseChartProps): void {
    this.setDynamicStyle();

    if (
      prevProps.mode !== this.props.mode ||
      prevProps.showLabelBoxes !== this.props.showLabelBoxes ||
      prevProps.halfDonut !== this.props.halfDonut
    ) {
      this.updatePluginRegistration();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  private updatePluginRegistration(): void {
    ChartJS.unregister(this.customLabelBoxesPlugin);
    ChartJS.unregister(this.halfDonutLabelPlugin);

    if (this.props.mode === "radar" && this.props.showLabelBoxes) {
      ChartJS.register(this.customLabelBoxesPlugin);
    }

    if (this.props.mode === "doughnut" && this.props.halfDonut) {
      ChartJS.register(this.halfDonutLabelPlugin);
    }
  }

  getProps(): BaseChartProps {
    const props: BaseChartProps = { ...this.props };

    // Remove all custom props that shouldn't be passed to the Chart component
    const customPropsToRemove = [
      "leftAxis",
      "rightAxis",
      "dataLabelKeyName",
      "dataDisplayLabelKeyName",
      "dataTooltipFooterDataLabel",
      "firstDataFieldKeyName",
      "firstDataBgColorKeyName",
      "secondDataFieldKeyName",
      "secondDataBgColorKeyName",
      "thirdDataFieldKeyName",
      "thirdDataBgColorKeyName",
      "firstDataBgColor",
      "secondDataBgColor",
      "thirdDataBgColor",
      "firstDataLegendLabel",
      "secondDataLegendLabel",
      "thirdDataLegendLabel",
      "firstDataLineStyle",
      "secondDataLineStyle",
      "thirdDataLineStyle",
      "additionalSeries",
      "fill",
      "maxYAxisValue",
      "maxYAxisValueRatio",
      "centerText",
      "showDataLabels",
      "dataLabelFormatter",
      "dataLabelPosition",
      "verticalDataLabels",
      "dataTargetLineKeyName",
      "multiTooltipContentField",
      "startAngle",
      "levelFrequency",
      "showLabelBoxes",
      "showLevelValueBoxes",
      "showLevelValues",
      "showPointLabels",
      "barThickness",
      "stacked",
      "lineColor",
      "lineThickness",
      "lineDotWidth",
      "showLineLabel",
      "lineLabelColor",
      "lineLabelPosition",
      "levelColor",
      "angleLinesColor",
      "pointLabelColor",
      "legendAlign",
      "legendFontSize",
      "dataSource",
      "beginAtZero",
      "legend",
      "halfDonut",
      "categoryHint",
      // CommonProps that shouldn't be passed to Chart
      "visibility",
      "visibilityByAuthorization",
      "editability",
      "rowIndex",
      "kuikaRef",
      "kDataType",
      "showCursorPointer",
      "className",
      "id",
      "style"
    ];

    customPropsToRemove.forEach((prop) => {
      if (prop in props) {
        delete (props as any)[prop];
      }
    });

    // Only return props that are valid for Chart.js
    const validChartProps = {
      mode: props.mode
    };

    return validChartProps as BaseChartProps;
  }

  getNumbersOfStyleProperties = (val: any) => {
    let numb: any = val?.match(/\d/g);
    numb = numb?.join("");
    return numb;
  };

  getRandomColor(key: string): string {
    if (
      this.colorMap[key] &&
      this.props.mode !== "pie" &&
      this.props.mode !== "doughnut" &&
      this.props.mode !== "polarArea"
    ) {
      return this.colorMap[key];
    }

    const guid = Guid.create();
    const guidValue = guid.toString().replace(/-/g, "");
    const randomNumber = parseInt(guidValue.substring(0, 8), 16);
    const randomIndex = randomNumber % this.colorPalette.length;

    const color = this.colorPalette[randomIndex];
    this.colorMap[key] = color;
    return color;
  }

  /**
   * Maps a per-series line style to a Chart.js `borderDash` pattern. "dashed" and
   * "dotted" produce dash arrays; anything else (including "solid"/undefined) returns
   * undefined so the line renders solid. Only meaningful for line-mode datasets.
   */
  private getLineDash(lineStyle: string | undefined): number[] | undefined {
    switch (lineStyle) {
      case "dashed":
        return [6, 4];
      case "dotted":
        return [2, 3];
      default:
        return undefined;
    }
  }

  getHeight(): string | undefined {
    if (this.props.style && this.props.style?.height) {
      return this.props.style.height.toString();
    }

    return undefined;
  }

  getMinHeight(): string | undefined {
    if (this.props.style && this.props.style?.minHeight) {
      return this.props.style.minHeight.toString();
    }
    return undefined;
  }

  getMaxHeight(): string | undefined {
    if (this.props.style && this.props.style?.maxHeight) {
      return this.props.style.maxHeight.toString();
    }

    return undefined;
  }

  getWidth(): string | undefined {
    if (this.props.style && this.props.style?.width) {
      return this.props.style.width.toString();
    }

    return undefined;
  }

  getMinWidth(): string | undefined {
    if (this.props.style && this.props.style?.minWidth) {
      return this.props.style.minWidth.toString();
    }

    return undefined;
  }

  getMaxWidth(): string | undefined {
    if (this.props.style && this.props.style?.maxWidth) {
      return this.props.style.maxWidth.toString();
    }

    return undefined;
  }

  getPaddingLeft(): string | undefined {
    if (this.props.style && this.props.style?.paddingLeft) {
      return `${this.props.style.paddingLeft.toString()}px`;
    }

    return undefined;
  }

  getPaddingTop(): string | undefined {
    if (this.props.style && this.props.style?.paddingTop) {
      return `${this.props.style.paddingTop.toString()}px`;
    }

    return undefined;
  }

  getPaddingRight(): string | undefined {
    if (this.props.style && this.props.style?.paddingRight) {
      return `${this.props.style.paddingRight.toString()}px`;
    }

    return undefined;
  }

  getPaddingBottom(): string | undefined {
    if (this.props.style && this.props.style?.paddingBottom) {
      return `${this.props.style.paddingBottom.toString()}px`;
    }

    return undefined;
  }

  private getDesignTimeSampleData(fill: boolean): ChartDataType {
    const mode = this.props.mode;
    const accent = "#689FF2";

    if (mode === "doughnut" || mode === "pie" || mode === "polarArea") {
      return {
        labels: ["Series A", "Series B", "Series C", "Series D"],
        datasets: [
          {
            label: "Sample",
            data: [45, 25, 18, 12],
            backgroundColor: this.colorPalette,
            borderColor: "#FFFFFF",
            borderWidth: 2,
            fill
          } as any
        ]
      };
    }

    if (mode === "radar") {
      return {
        labels: ["Speed", "Quality", "Reach", "Cost", "Trust", "Design"],
        datasets: [
          {
            label: "Sample",
            data: [80, 90, 75, 95, 85, 70],
            backgroundColor: "rgba(104, 159, 242, 0.2)",
            borderColor: accent,
            pointBackgroundColor: accent,
            pointBorderColor: "#FFFFFF",
            pointBorderWidth: 2,
            pointRadius: 3,
            fill: true
          } as any
        ]
      };
    }

    const series = resolveSampleSeries(this.props.categoryHint);

    if (mode === "line") {
      return {
        labels: series.labels,
        datasets: [
          {
            label: "Sample",
            data: series.values,
            backgroundColor: "rgba(104, 159, 242, 0.15)",
            borderColor: accent,
            pointBackgroundColor: accent,
            pointBorderColor: "#FFFFFF",
            pointBorderWidth: 2,
            pointRadius: 4,
            tension: 0.35,
            fill: true
          } as any
        ]
      };
    }

    return {
      labels: series.labels,
      datasets: [
        {
          label: "Sample",
          data: series.values,
          backgroundColor: this.colorPalette,
          borderRadius: 6,
          borderSkipped: false,
          fill
        } as any
      ]
    };
  }

  getData(): ChartDataType {
    let labels: string[] = [];
    let displayLabels: string[] = [];
    let tooltipFooterLabels: string[] = [];
    const dataSets: any[] = [];
    const fill: boolean = this.props.fill ?? false;
    const barThickness = this.props.barThickness;

    if (window.kuika?.dashboardState === 1) {
      return this.getDesignTimeSampleData(fill);
    }

    if (!this.props.dataSource || !Array.isArray(this.props.dataSource) || this.props.dataSource.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    if (this.props.dataDisplayLabelKeyName && this.props.dataDisplayLabelKeyName.length > 0) {
      labels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataDisplayLabelKeyName)]) || [];
    } else {
      labels =
        this.props.dataSource?.map((x, idx) => {
          return `Label ${idx}`;
        }) || [];
    }

    if (this.props.firstDataFieldKeyName) {
      displayLabels =
        this.props.dataSource?.map((x, idx) => {
          return x[KMainFunctions.toCamelCase(this.props.firstDataFieldKeyName)];
        }) || [];
    } else if (this.props.dataLabelKeyName && this.props.dataLabelKeyName.length > 0) {
      displayLabels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataLabelKeyName)]) || [];
    } else {
      displayLabels =
        this.props.dataSource?.map((x, idx) => {
          return `${idx}`;
        }) || [];
    }

    const tooltipTitleLabels =
      this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataLabelKeyName)]) || [];

    if (this.props.dataTooltipFooterDataLabel && this.props.dataTooltipFooterDataLabel.length > 0) {
      tooltipFooterLabels =
        this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(this.props.dataTooltipFooterDataLabel)]) || [];
    } else {
      tooltipFooterLabels =
        this.props.dataSource?.map((x, idx) => {
          return `${idx}`;
        }) || [];
    }

    const createDataset = (
      fieldKeyName: string,
      bgColorKeyName: string,
      datasetLabel: string,
      datasetIndex: number,
      fixedBgColor?: string,
      legendLabel?: string,
      lineStyle?: string
    ) => {
      let bgColor: string[] = [];

      if (fixedBgColor && fixedBgColor.length > 0) {
        bgColor = this.props.dataSource?.map(() => fixedBgColor);
      } else if (bgColorKeyName && bgColorKeyName.length > 0) {
        bgColor = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(bgColorKeyName)]);
      } else {
        bgColor = this.props.dataSource?.map((x) => this.getRandomColor(fieldKeyName));
      }

      const data: any[] = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(fieldKeyName)]);

      const datasetDisplayLabels = this.props.dataSource?.map((x) => x[KMainFunctions.toCamelCase(fieldKeyName)]) || [];

      const dataset: {
        label: string;
        data: any[];
        backgroundColor: string[];
        fill: boolean;
        displayLabels: string[];
        tooltipFooterLabels: string[];
        tooltipTitleLabels?: any[];
        borderColor?: string[];
        pointBackgroundColor?: string[];
        pointBorderColor?: string[];
        barThickness?: number;
        borderWidth?: number;
        borderDash?: number[];
        pointRadius?: number;
        pointHoverRadius?: number;
        type?: string;
        stack?: string;
      } = {
        label: legendLabel || datasetLabel || `${datasetIndex}`,
        data,
        backgroundColor: bgColor,
        fill,
        displayLabels: datasetDisplayLabels,
        tooltipFooterLabels,
        tooltipTitleLabels
      };

      if (this.props.mode === "radar") {
        dataset.borderColor = bgColor;
        dataset.pointBackgroundColor = bgColor;
        dataset.pointBorderColor = bgColor;
      }

      if (this.props.mode === "bar" && barThickness) {
        dataset.barThickness = barThickness;
      }

      if (this.props.mode === "bar" && this.props.stacked) {
        dataset.stack = "stack-0";
      }

      if (this.props.mode === "line") {
        if (this.props.lineColor) {
          dataset.borderColor = Array(data.length).fill(this.props.lineColor);
        }

        if (this.props.lineThickness !== undefined && this.props.lineThickness !== null) {
          const thickness = Math.min(Math.max(this.props.lineThickness, 1), 10) * 2;
          dataset.borderWidth = thickness;
        }

        if (this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null) {
          const dotWidth = Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2;
          dataset.pointRadius = dotWidth;
          dataset.pointHoverRadius = dotWidth + 2;
        }

        const borderDash = this.getLineDash(lineStyle);
        if (borderDash) {
          dataset.borderDash = borderDash;
        }

        dataset.pointBackgroundColor = bgColor || dataset.borderColor;
        dataset.pointBorderColor = bgColor || dataset.borderColor;
      }

      return dataset;
    };

    // The fixed first three series (flat props) and any additional series (KOA-18057)
    // are normalised into one ordered list, then each non-empty series becomes a
    // dataset. This is the single place that unifies the "3 named slots + array"
    // wire format, so a chart can render an unbounded number of series.
    const series: ChartSeriesInput[] = [
      {
        fieldKeyName: this.props.firstDataFieldKeyName,
        bgColorKeyName: this.props.firstDataBgColorKeyName,
        bgColor: this.props.firstDataBgColor,
        legendLabel: this.props.firstDataLegendLabel,
        lineStyle: this.props.firstDataLineStyle
      },
      {
        fieldKeyName: this.props.secondDataFieldKeyName,
        bgColorKeyName: this.props.secondDataBgColorKeyName,
        bgColor: this.props.secondDataBgColor,
        legendLabel: this.props.secondDataLegendLabel,
        lineStyle: this.props.secondDataLineStyle
      },
      {
        fieldKeyName: this.props.thirdDataFieldKeyName,
        bgColorKeyName: this.props.thirdDataBgColorKeyName,
        bgColor: this.props.thirdDataBgColor,
        legendLabel: this.props.thirdDataLegendLabel,
        lineStyle: this.props.thirdDataLineStyle
      },
      ...(Array.isArray(this.props.additionalSeries) ? this.props.additionalSeries : [])
    ];

    series.forEach((s: ChartSeriesInput, index: number) => {
      if (s.fieldKeyName && s.fieldKeyName.length > 0) {
        dataSets.push(
          createDataset(
            s.fieldKeyName,
            s.bgColorKeyName,
            s.fieldKeyName,
            index + 1,
            s.bgColor,
            s.legendLabel,
            s.lineStyle
          )
        );
      }
    });

    if (this.props.dataTargetLineKeyName && this.props.dataTargetLineKeyName?.length > 0) {
      const targetData: any[] = this.props.dataSource?.map(
        (x) => x[KMainFunctions.toCamelCase(this.props.dataTargetLineKeyName)]
      );

      const targetLineConfig = {
        label: this.props.dataTargetLineKeyName || "Target",
        data: targetData,
        type: "line",
        borderColor: this.props.lineColor || "#000000",
        borderWidth:
          this.props.lineThickness !== undefined && this.props.lineThickness !== null
            ? Math.min(Math.max(this.props.lineThickness, 1), 10) * 2
            : 2,
        fill: false,
        pointRadius:
          this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null
            ? Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2
            : 4,
        pointBackgroundColor: this.props.lineColor || "#000000",
        pointBorderColor: this.props.lineColor || "#000000",
        pointBorderWidth: 1,
        pointHoverRadius:
          this.props.lineDotWidth !== undefined && this.props.lineDotWidth !== null
            ? Math.min(Math.max(this.props.lineDotWidth, 1), 10) * 2 + 2
            : 6,
        pointHoverBackgroundColor: this.props.lineColor || "#000000",
        pointHoverBorderColor: this.props.lineColor || "#000000",
        pointHoverBorderWidth: 2,
        order: -1,
        isTargetLine: true,
        displayLabels: targetData
      };

      dataSets.push(targetLineConfig);
    }

    return {
      labels,
      datasets: dataSets
    };
  }

  getDynamicCss = (): string => {
    const className: string = this.canvasId;
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    const style = this.props.style || {};
    const { height, maxHeight, minHeight, width, minWidth, maxWidth } = style;

    if (height) {
      const isPercentage = typeof height === "string" && height.includes("%");
      if (isPercentage) {
        result += `.${className} {
          height: ${height} !important;
        }
        .${className} canvas {
          height: 100% !important;
        }`;
      } else if (!maxHeight && !minHeight) {
        result += `.${className} {
          height: unset !important;
          max-height: ${height} !important;
          min-height: ${height} !important;
        }`;
      }
    }

    if ((width && !maxWidth && !minWidth) || minWidth < width || maxWidth > width) {
      result += `.${className} {
        width: unset !important;
        max-width: ${width} !important;
        min-width: ${width} !important;
      }`;
    }

    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getFormattedValue(value) {
    if (window.kuika?.isDesignTime) {
      return value;
    }
    let { dataLabelFormatter: formatter } = this.props;
    if (formatter && value && value?.toString()?.trim() !== "") {
      switch (formatter) {
        case "money":
          if (Number.isNaN(Number(value))) {
            return "0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
            .format(Number(value))
            .replace("₺", "");

        case "money-var-1":
          if (Number.isNaN(Number(value))) {
            return "0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })
            .format(Number(value))
            .replace("₺", "");

        case "money-tl":
          if (Number.isNaN(Number(value))) {
            return "₺0.00";
          }

          return getCachedNumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(Number(value));

        case "percent":
          return `${value}%`;

        case "fractional-2":
          const number2 = Number(value);
          return isNaN(number2) ? value : number2.toFixed(2);

        case "fractional-5":
          const number5 = Number(value);
          return isNaN(number5) ? value : number5.toFixed(5);
        default:
          return value;
      }
    } else {
      // Locale-format numeric chart values — whether they arrive as JS numbers or as
      // numeric strings (Kuika datasources may serialise SQL numbers either way). A
      // plain integer/decimal like 24568011 or 24568.11 becomes "24.568.011" / "24.568,11"
      // (tr) or "24,568,011" / "24,568.11" (en). Strings that are NOT a clean number —
      // category labels ("Ocak"), or values that already contain separators
      // ("24.568.011") — are left untouched (Number() yields NaN), so they fall through
      // to the existing text path and are never double-formatted or mangled.
      const numericValue: number | undefined = this.toFiniteChartNumber(value);
      if (numericValue !== undefined) {
        return this.formatNumberLocale(numericValue);
      }

      if (value && typeof value === "string") {
        const kuikaCulture = localStorage.getItem("ml");
        const isLangTR = kuikaCulture?.toLowerCase() === "tr_tr";

        if (this.props.style?.textTransform === "lowercase" && (value.includes("İ") || value.includes("I"))) {
          if (isLangTR) {
            value = value.replace(/I/g, "ı");
          }
          value = value.replace(/İ/g, "i");
        } else if (this.props.style?.textTransform === "uppercase" && (value.includes("i") || value.includes("ı"))) {
          if (isLangTR) {
            value = value.replace(/i/g, "İ");
          }
          value = value.replace(/ı/g, "I");
        } else if (this.props.style?.textTransform === "capitalize") {
          if (isLangTR && value[0] === "i") {
            value = value.replace("i", "İ");
          } else if (value[0] === "ı") {
            value = value.replace("ı", "I");
          }
        }
      }

      return value?.toString();
    }
  }

  /**
   * Resolves the active app locale from the same source label.tsx uses for
   * dates: the `ml` localStorage key (e.g. "tr_TR" / "en_US"). Converted to a
   * BCP-47 tag ("tr-TR") for Intl; falls back to "en-US".
   */
  private getLocale(): string {
    const ml: string = localStorage.getItem("ml") || "";
    return ml ? ml.replace("_", "-") : "en-US";
  }

  /**
   * Formats a number with the active locale's grouping and decimal separators
   * (tr -> "2.344,05", en -> "2,344.05", fr -> "2 344,05"). Used for axis ticks
   * and numeric tooltip / data-label values so the chart matches the app
   * language.
   *
   * Some locales (e.g. fr-FR) group with a narrow no-break space (U+202F) or a
   * no-break space (U+00A0). Chart.js renders ticks / labels on a <canvas>, where
   * those glyphs are frequently dropped by the chart font — making a grouped
   * French number ("24 568 011") look unseparated ("24568011"). Normalising both
   * to a regular space keeps the value locale-correct while guaranteeing the
   * separator is actually visible on the canvas.
   */
  private formatNumberLocale(value: number): string {
    const formatted: string = getCachedNumberFormat(this.getLocale(), { maximumFractionDigits: 2 }).format(value);
    return formatted.replace(/[\u202f\u00a0]/g, " ");
  }

  /**
   * Returns the finite numeric value of a chart datum, accepting both JS numbers and
   * clean numeric strings ("24568011", "24568.11", " 12 "). Returns undefined for
   * anything that is not a clean number — empty/whitespace, category text ("Ocak"),
   * NaN/Infinity, or strings that already carry grouping/decimal separators
   * ("24.568.011", "24,568,011") — so the caller leaves those untouched.
   */
  private toFiniteChartNumber(value: unknown): number | undefined {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "string") {
      const trimmed: string = value.trim();
      if (trimmed === "") {
        return undefined;
      }
      const parsed: number = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  /**
   * Builds the rows shown in the multi-field tooltip. Each comma-separated entry
   * is a column name, optionally with a fixed display label via "column:Label"
   * syntax (no colon -> the column name is used as the label, legacy behaviour).
   */
  private getMultiTooltipRows(dataIndex: number): string[] {
    if (!this.props.multiTooltipContentField) {
      return [];
    }
    const fields: string[] = this.props.multiTooltipContentField.split(",");
    return fields.map((field: string): string => {
      const trimmed: string = field.trim();
      const separatorIndex: number = trimmed.indexOf(":");
      const column: string = separatorIndex >= 0 ? trimmed.slice(0, separatorIndex).trim() : trimmed;
      const fieldLabel: string = separatorIndex >= 0 ? trimmed.slice(separatorIndex + 1).trim() : trimmed;
      const value = this.props.dataSource[dataIndex][KMainFunctions.toCamelCase(column)];
      return `${fieldLabel}: ${this.getFormattedValue(value)}`;
    });
  }

  /**
   * Computes the raw maximum value used to size the Y axis. For grouped charts
   * this is the largest single data point; for stacked bars it must be the
   * largest per-category SUM (otherwise the stacked column overflows the axis
   * ceiling). Line / target-line datasets are never stacked, so they are
   * measured individually even in stacked mode.
   */
  private computeRawMaxValue(datasets: ChartDatasetLike[] | undefined): number {
    if (!datasets || datasets.length === 0) {
      return 0;
    }

    const isStacked: boolean = (this.props.stacked ?? false) && this.props.mode === "bar";

    if (!isStacked) {
      const perDatasetMax: number[] = datasets.map((ds: ChartDatasetLike): number => {
        const values: number[] = (ds.data ?? []).map((v: unknown): number => Number(v) || 0);
        return values.length > 0 ? Math.max(...values) : 0;
      });
      return perDatasetMax.length > 0 ? Math.max(...perDatasetMax) : 0;
    }

    const categoryCount: number = Math.max(0, ...datasets.map((ds: ChartDatasetLike): number => ds.data?.length ?? 0));
    let maxValue: number = 0;
    for (let i = 0; i < categoryCount; i++) {
      let stackedSum: number = 0;
      let lineMax: number = 0;
      datasets.forEach((ds: ChartDatasetLike): void => {
        const value: number = Number(ds.data?.[i]) || 0;
        if (ds.type === "line" || ds.isTargetLine) {
          lineMax = Math.max(lineMax, value);
        } else {
          stackedSum += value;
        }
      });
      maxValue = Math.max(maxValue, stackedSum, lineMax);
    }
    return maxValue;
  }

  /**
   * When no explicit `maxYAxisValue` is set, rounds the Y axis ceiling up to a
   * "nice" value and returns the matching tick step, so the top of the axis is
   * a round number (e.g. raw max 523 -> 600 with step 100) instead of the raw
   * data maximum. Targets ~6 intervals so the intermediate ticks also land on
   * round numbers. Returns undefined for non-positive / non-finite input so the
   * caller can fall back to Chart.js auto-scaling.
   */
  private computeNiceAxis(rawMax: number): NiceAxis | undefined {
    if (!Number.isFinite(rawMax) || rawMax <= 0) {
      return undefined;
    }

    const targetTicks: number = 6;
    const rawStep: number = rawMax / targetTicks;
    const magnitude: number = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized: number = rawStep / magnitude;

    let niceFactor: number;
    if (normalized <= 1) {
      niceFactor = 1;
    } else if (normalized <= 2) {
      niceFactor = 2;
    } else if (normalized <= 2.5) {
      niceFactor = 2.5;
    } else if (normalized <= 5) {
      niceFactor = 5;
    } else {
      niceFactor = 10;
    }

    const stepSize: number = niceFactor * magnitude;
    const max: number = Math.ceil(rawMax / stepSize) * stepSize;
    return { max, stepSize };
  }

  render(): ReactNode {
    const data = this.getData();
    let labels = [];

    if (this.props.children) {
      if (Array.isArray(this.props.children) && this.props.children.length > 1) {
        labels = this.props.children.map((child: any) => {
          return {
            text: child?.props?.text ?? "",
            font: {
              size: this.getNumbersOfStyleProperties(child?.props?.style?.fontSize) ?? "14",
              weight: child?.props?.style?.fontWeight ?? "medium",
              family: child?.props?.style?.fontFamily ?? "Arial"
            },
            color: child?.props?.style?.color ?? "black"
          };
        });
      } else if (this.props.children) {
        labels = [
          {
            text: this.props.children?.props?.text ?? "",
            font: {
              size: this.getNumbersOfStyleProperties(this.props.children?.props?.style?.fontSize) ?? "14",
              weight: this.props.children?.props?.style?.fontWeight ?? "medium",
              family: this.props.children?.props?.style?.fontFamily ?? "Arial"
            },
            color: this.props.children?.props?.style?.color ?? "black"
          }
        ];
      }
    }

    const maxYValue: number = this.computeRawMaxValue(data?.datasets as ChartDatasetLike[] | undefined);
    const hasExplicitYMax: boolean = this.props.maxYAxisValue !== undefined && this.props.maxYAxisValue !== null;
    // Master's ratio feature wins when configured: with no explicit max, the
    // Y-axis top is pushed to `dataMax * ratio` to leave headroom above the
    // tallest value. Guarded to finite, positive maxima so empty/negative
    // datasets fall back to the default behaviour instead of NaN/-Infinity.
    const maxYAxisValueRatio: number | undefined = this.props.maxYAxisValueRatio;
    const ratioBasedMaxYValue: number | undefined =
      typeof maxYAxisValueRatio === "number" && maxYAxisValueRatio > 0 && Number.isFinite(maxYValue) && maxYValue > 0
        ? maxYValue * maxYAxisValueRatio
        : undefined;
    // Our nice-axis rounding only applies when neither an explicit max nor a
    // ratio is set, so the ratio feature's exact output is never re-rounded.
    const niceYAxis: NiceAxis | undefined =
      hasExplicitYMax || ratioBasedMaxYValue !== undefined ? undefined : this.computeNiceAxis(maxYValue);
    const isDesignTimePreview = window.kuika?.dashboardState === 1;
    const designSystemFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

    // Value (Y) axis ticks: always locale-format the numbers; carry the
    // nice-axis step and the design-time-preview styling when present. The X
    // axis is left untouched because it renders category labels, not numbers.
    const yAxisTicks: AxisTicks = {
      callback: (value: number | string): string =>
        typeof value === "number" ? this.formatNumberLocale(value) : `${value}`
    };
    if (niceYAxis) {
      yAxisTicks.stepSize = niceYAxis.stepSize;
    }
    if (isDesignTimePreview) {
      yAxisTicks.color = "#94A3B8";
      yAxisTicks.padding = 8;
      yAxisTicks.font = { size: 11, family: (this.props.style?.fontFamily as string) ?? designSystemFont };
    }

    const options: any = {
      plugins: {
        legend: {
          position: this.props.legend,
          display:
            typeof this.props.legend === "string" &&
            this.props.legend &&
            ["top", "right", "bottom", "left"].indexOf(this.props.legend) > -1,
          align:
            (this.props.style?.textAlign as any) === "-webkit-right"
              ? "end"
              : (this.props.style?.textAlign as any) === "-webkit-left"
                ? "start"
                : "center",

          labels: {
            boxWidth: isDesignTimePreview ? 8 : 15,
            boxHeight: isDesignTimePreview ? 8 : 15,
            usePointStyle: true,
            pointStyle: "circle",
            ...(isDesignTimePreview && {
              padding: 14,
              color: "#475569"
            }),
            font: {
              size: this.props.style?.fontSize ?? 12,
              ...(isDesignTimePreview && {
                family: (this.props.style?.fontFamily as string) ?? designSystemFont
              })
            },
            ...(this.props.mode === "line" && {
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index;
                const chart = legend.chart;
                const meta = chart.getDatasetMeta(index);

                meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : !meta.hidden;

                chart.update();
              },
              generateLabels: (chart) => {
                const datasets = chart.data.datasets;
                const { legendAlign } = this.props;
                return chart._getSortedDatasetMetas().map((meta) => {
                  const style = meta.controller.getStyle(meta.index);
                  return {
                    text: datasets[meta.index].label,
                    fillStyle: style.borderColor || style.backgroundColor,
                    strokeStyle: style.borderColor || style.backgroundColor,
                    lineWidth: 0,
                    hidden: !meta.visible,
                    index: meta.index,
                    align: legendAlign
                  };
                });
              }
            })
          }
        },
        ...(labels &&
          labels.length > 0 &&
          !this.props.halfDonut && {
            doughnutLabel: {
              labels
            }
          }),
        ...(labels &&
          labels.length > 0 &&
          this.props.halfDonut && {
            halfDonutLabel: {
              labels
            }
          }),
        ChartDataLabels,
        tooltip: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          bodySpacing: 4,
          padding: 10,
          callbacks: window.kuika?.isDesignTime
            ? undefined
            : {
                title: this.props.dataLabelKeyName
                  ? (context: any) => {
                      if (context[0]?.dataset?.type !== "line" && context[0]?.dataset?.tooltipTitleLabels) {
                        return context[0].dataset.tooltipTitleLabels[context[0].dataIndex];
                      }
                      return context.label;
                    }
                  : undefined,
                label: this.props.multiTooltipContentField
                  ? (context: any) => this.getMultiTooltipRows(context.dataIndex)
                  : this.props.dataTooltipFooterDataLabel
                    ? (context: any) => {
                        if (context?.dataset?.tooltipFooterLabels && context?.dataset?.type !== "line") {
                          return null;
                        }
                        if (context?.dataset?.type === "line") {
                          return `${context.dataset.label}:${context.formattedValue}`;
                        }
                        return context.label;
                      }
                    : (context: any) => {
                        const parsedValue: unknown = context?.parsed?.y;
                        const formatted: string =
                          typeof parsedValue === "number"
                            ? this.getFormattedValue(parsedValue)
                            : context.formattedValue;
                        return context?.dataset?.label ? `${context.dataset.label}: ${formatted}` : `${formatted}`;
                      },
                footer: this.props.dataTooltipFooterDataLabel
                  ? (context: any) => {
                      if (context[0]?.dataset?.tooltipFooterLabels && context[0]?.dataset?.type !== "line") {
                        return context[0].dataset.tooltipFooterLabels[context[0].dataIndex];
                      }
                      return null;
                    }
                  : undefined,
                labelPointStyle: (context) => {
                  return {
                    pointStyle: "circle",
                    rotation: 0
                  };
                },
                labelColor: (context) => {
                  const dataset = context.dataset;
                  const colors = dataset.backgroundColor || dataset.borderColor;
                  const color = Array.isArray(colors) ? colors[context.dataIndex] : colors;

                  return {
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 1,
                    borderRadius: 3
                  };
                }
              },
          displayColors: true,
          titleMarginBottom: 8,
          ...(isDesignTimePreview && {
            backgroundColor: "rgba(15, 23, 42, 0.92)",
            titleColor: "#FFFFFF",
            bodyColor: "#E2E8F0",
            footerColor: "#94A3B8",
            borderColor: "rgba(255, 255, 255, 0.08)",
            borderWidth: 1,
            cornerRadius: 8
          }),
          itemSort: (a, b) => {
            if (a.datasetIndex < b.datasetIndex) return -1;
            if (a.datasetIndex > b.datasetIndex) return 1;
            return 0;
          },
          titleFont: {
            weight: isDesignTimePreview ? "600" : "bold",
            size: 12,
            ...(isDesignTimePreview && {
              family: (this.props.style?.fontFamily as string) ?? designSystemFont
            })
          },
          bodyFont: {
            size: 11,
            ...(isDesignTimePreview && {
              family: (this.props.style?.fontFamily as string) ?? designSystemFont
            })
          },
          footerFont: {
            weight: "normal",
            style: "italic",
            size: 11,
            ...(isDesignTimePreview && {
              family: (this.props.style?.fontFamily as string) ?? designSystemFont
            })
          }
        },
        datalabels: {
          formatter: (value: any, context: any) => {
            const isTargetLine = context.dataset.isTargetLine;

            if (isTargetLine) {
              if (!this.props.showLineLabel) return null;
              if (window.kuika?.isDesignTime) return value;
              const index = context.dataIndex;
              return context.dataset.displayLabels && context.dataset.displayLabels[index]
                ? this.getFormattedValue(context.dataset.displayLabels[index])
                : null;
            }

            if (!this.props.showDataLabels) return null;
            if (window.kuika?.isDesignTime) return value;
            const index = context.dataIndex;
            return context.dataset.displayLabels && context.dataset.displayLabels[index]
              ? this.getFormattedValue(context.dataset.displayLabels[index])
              : null;
          },
          color: (context: any) => {
            const isTargetLine = context.dataset.isTargetLine;
            if (isTargetLine) {
              return this.props.lineLabelColor ?? this.props.lineColor ?? "black";
            }
            return this.props.style?.color ?? "black";
          },
          font: {
            size: this.props.style?.fontSize ?? 14,
            weight: this.props.style?.fontWeight ?? "bold",
            family: this.props.style?.fontFamily ?? "Arial"
          },
          align: (context: any) => {
            if (!context) return "center";
            const isTargetLine = context.dataset?.isTargetLine;
            if (isTargetLine && this.props.lineLabelPosition) {
              return this.props.lineLabelPosition === "top"
                ? "top"
                : this.props.lineLabelPosition === "bottom"
                  ? "bottom"
                  : "center";
            }
            if (this.props.mode === "bar" && this.props.verticalDataLabels) {
              return "top";
            }
            if (this.props.mode === "line" && this.props.dataLabelPosition) {
              return this.props.dataLabelPosition === "top"
                ? "top"
                : this.props.dataLabelPosition === "bottom"
                  ? "bottom"
                  : "center";
            }
            return "center";
          },
          anchor: (context: any) => {
            if (!context) return "center";
            const isTargetLine = context.dataset?.isTargetLine;
            if (isTargetLine && this.props.lineLabelPosition) {
              return this.props.lineLabelPosition === "top"
                ? "end"
                : this.props.lineLabelPosition === "bottom"
                  ? "start"
                  : "center";
            }
            if (this.props.mode === "bar" && this.props.verticalDataLabels) {
              return "end";
            }
            if (this.props.mode === "line" && this.props.dataLabelPosition) {
              return this.props.dataLabelPosition === "top"
                ? "end"
                : this.props.dataLabelPosition === "bottom"
                  ? "start"
                  : "center";
            }
            return "center";
          },
          rotation: (context: any) => {
            if (!context) return 0;
            if (this.props.mode === "bar" && this.props.verticalDataLabels) {
              return -90;
            }
            return 0;
          }
        }
      },
      ...(isDesignTimePreview && {
        layout: {
          padding: { top: 8, right: 12, bottom: 8, left: 12 }
        }
      }),
      scales: {
        y: {
          display: this.props.leftAxis,
          beginAtZero: this.props.beginAtZero,
          max: hasExplicitYMax ? this.props.maxYAxisValue : (ratioBasedMaxYValue ?? niceYAxis?.max),
          ...(this.props.stacked && this.props.mode === "bar" && { stacked: true }),
          ticks: yAxisTicks,
          ...(isDesignTimePreview && {
            grid: { color: "rgba(15, 23, 42, 0.06)", drawBorder: false },
            border: { display: false }
          })
        },
        x: {
          display: this.props.rightAxis,
          beginAtZero: this.props.beginAtZero,
          ...(this.props.stacked && this.props.mode === "bar" && { stacked: true }),
          ...(isDesignTimePreview && {
            grid: { display: false, drawBorder: false },
            border: { display: false },
            ticks: {
              color: "#94A3B8",
              padding: 6,
              font: {
                size: 11,
                family: (this.props.style?.fontFamily as string) ?? designSystemFont
              }
            }
          })
        },
        ...(this.props.mode === "radar" && {
          r: {
            ...(!window.kuika?.isDesignTime && {
              ticks: {
                display:
                  !this.props.dataSource || !Array.isArray(this.props.dataSource) || this.props.dataSource.length === 0
                    ? false
                    : (this.props.showLevelValueBoxes ?? true),
                stepSize: this.props.levelFrequency ?? 1
              }
            }),
            beginAtZero: this.props.beginAtZero,
            ...(this.props.pointLabelColor && {
              pointLabels: {
                color: this.props.pointLabelColor
              }
            }),
            ...(this.props.levelColor && {
              grid: {
                color: this.props.levelColor
              }
            }),
            ...(this.props.angleLinesColor && {
              angleLines: {
                color: this.props.angleLinesColor
              }
            })
          }
        })
      },
      responsive: true
    };

    if (this.props.mode === "doughnut" && this.props.halfDonut) {
      options.rotation = -90;
      options.circumference = 180;
    }

    return (
      <Chart
        id={this.canvasId}
        type={this.props.mode}
        {...this.getProps()}
        options={_.cloneDeep(options)}
        className={this.props.className || this.canvasId}
        data={data}
        data-testid="chart"
        style={{
          height: this.getHeight(),
          minHeight: this.getMinHeight(),
          maxHeight: this.getMaxHeight(),
          width: this.getWidth(),
          minWidth: this.getMinWidth(),
          maxWidth: this.getMaxWidth(),
          paddingLeft: this.getPaddingLeft(),
          paddingRight: this.getPaddingRight(),
          paddingTop: this.getPaddingTop(),
          paddingBottom: this.getPaddingBottom(),
          backgroundImage: this.props.style?.backgroundImage,
          backgroundColor: this.props.style?.backgroundColor
        }}
      />
    );
  }
}
const baseChart = withCommonEvents(BaseChart);
export { baseChart as BaseChart };
